import { fade, makeStyles } from '@material-ui/core/styles';

// docs/ux/email-screens-mockup.html ile birebir: renk token'ları mockup'tan, vurgu rengi CRM temasından.
// Yazı boyutları tek ölçek: 12 / 14 / 17 px.
export const MUTED = '#5b6b75';
export const LINE = '#e2e7ea';
export const TINT = '#e6edf2';
export const PAPER_2 = '#f4f6f8';
export const OK = '#2e7d32';
export const OK_BG = '#e8f5e9';
export const WARN = '#b26a00';
export const WARN_BG = '#fff3e0';
export const CRIT = '#c62828';
export const CRIT_BG = '#fdeaea';
export const IDLE = '#78909c';

// Durum noktası rengi (liste ile aynı kural): kapalı gri, bekleyen turuncu, diğer yeşil.
export const statusDotColor = (key, isTerminal) => {
  if (isTerminal) return IDLE;
  return /wait|pend/i.test(key || '') ? WARN : OK;
};
const FS_SMALL = 12;
const FS_BASE = 14;
const FS_TITLE = 17;

export default makeStyles(theme => {
  const font = theme.typography.fontFamily;
  const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    gap: 6,
    padding: '2px 9px',
    borderRadius: 12,
    fontFamily: font,
    fontSize: FS_SMALL,
    lineHeight: 1.5,
    fontWeight: 500,
    color: theme.palette.text.primary,
    backgroundColor: PAPER_2,
    border: `1px solid ${LINE}`
  };
  return {
    // CRM teması global gövde fontu tanımlamaz; düz span/div/Link'ler serif'e düşmesin diye
    // tüm kapsayıcılar tema fontunu (Typography ile aynı aile) açıkça taşır.
    // Düzen: mesaj zaman çizelgesi kendi kaydırma alanındadır, yanıt çubuğu altında sabit durur
    // (üst atalarda overflow olduğundan position: sticky sayfa kaydırmasında çalışmaz).
    root: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 480,
      fontFamily: font,
      fontSize: FS_BASE,
      lineHeight: 1.5,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${LINE}`,
      borderRadius: 6,
      // Mockup düğme ölçüleri: 4 px radius, 6x14 (küçük 4x10), sentence case, 500.
      '& .MuiButton-root': {
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: 4,
        padding: '6px 14px',
        lineHeight: 1.5
      },
      '& .MuiButton-sizeSmall': { padding: '4px 10px', fontSize: 13 },
      '& .MuiButton-outlined': { borderColor: LINE },
      '& .MuiChip-root': { fontFamily: font }
    },
    toolbar: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      padding: '10px 14px',
      borderBottom: `1px solid ${LINE}`
    },
    chip: {
      fontSize: 13,
      fontWeight: 400,
      height: 28,
      borderRadius: 14,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${LINE}`
    },
    chipSelected: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderColor: theme.palette.primary.main,
      '&:hover, &:focus': { backgroundColor: theme.palette.primary.dark }
    },
    center: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: MUTED,
      padding: 24,
      textAlign: 'center'
    },
    crumbs: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
      fontFamily: font,
      fontSize: 13,
      color: MUTED,
      padding: '10px 16px'
    },
    crumbLink: {
      fontFamily: font,
      fontSize: 13,
      color: MUTED,
      textDecoration: 'none',
      cursor: 'pointer',
      verticalAlign: 'baseline',
      '&:hover': { color: theme.palette.text.primary, textDecoration: 'underline' }
    },
    header: {
      display: 'grid',
      gap: 8,
      fontFamily: font,
      padding: '12px 16px',
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${LINE}`
    },
    headerTitle: {
      fontSize: FS_TITLE,
      lineHeight: 1.5,
      fontWeight: 500,
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    caseRef: { fontWeight: 400, marginRight: 8 },
    headerMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px 14px',
      alignItems: 'center',
      fontFamily: font,
      fontSize: FS_BASE,
      color: MUTED
    },
    metaOwner: { color: MUTED },
    metaLabel: { color: MUTED, marginRight: 4 },
    badge: pill,
    badgeClosed: {},
    pillUrgent: { backgroundColor: CRIT_BG, color: CRIT, borderColor: 'transparent' },
    pillHigh: { backgroundColor: WARN_BG, color: WARN, borderColor: 'transparent' },
    pillMuted: { color: MUTED },
    dot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flex: 'none' },
    body: {
      fontFamily: font,
      flex: 1,
      display: 'flex',
      alignItems: 'flex-start',
      [theme.breakpoints.down('sm')]: { flexDirection: 'column', alignItems: 'stretch' }
    },
    timeline: {
      flex: 1,
      minWidth: 0,
      padding: '12px 16px',
      display: 'grid',
      gap: 10,
      alignContent: 'start',
      boxSizing: 'border-box',
      // Kendi kaydırma alanı: uzun yazışmada yanıt çubuğu görünür alanda kalır.
      maxHeight: 'max(320px, calc(100vh - 440px))',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: { maxHeight: 'none', overflowY: 'visible' }
    },
    timelineHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8
    },
    timelineTitle: { fontSize: FS_BASE, fontWeight: 500, color: theme.palette.text.primary },
    side: {
      fontFamily: font,
      width: 320,
      minWidth: 320,
      backgroundColor: theme.palette.background.paper,
      borderLeft: `1px solid ${LINE}`,
      [theme.breakpoints.down('sm')]: {
        width: 'auto',
        minWidth: 0,
        borderLeft: 'none',
        borderTop: `1px solid ${LINE}`
      }
    },
    sideTabs: { borderBottom: `1px solid ${LINE}`, minHeight: 40, padding: '0 6px' },
    sideTab: {
      minHeight: 40,
      minWidth: 0,
      padding: '10px 4px',
      fontSize: FS_BASE,
      fontWeight: 500,
      textTransform: 'none',
      color: MUTED,
      '&.Mui-selected': { color: theme.palette.text.primary }
    },
    // Yanıt çubuğu: opak zemin, üst çizgi, çift çerçeve yok. Altta, kaydırma alanının hemen dışında durur.
    replyBar: {
      position: 'sticky',
      fontFamily: font,
      bottom: 0,
      zIndex: 2,
      padding: '10px 16px',
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${LINE}`,
      borderRadius: '0 0 6px 6px',
      boxShadow: '0 -2px 8px rgba(29,51,67,.06)',
      '&:empty': { display: 'none' }
    },
    sideSection: { padding: '12px 14px', borderBottom: `1px solid ${LINE}` },
    sideTitle: {
      fontSize: FS_BASE,
      fontWeight: 500,
      color: theme.palette.text.primary,
      marginBottom: 8
    },
    card: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${LINE}`,
      borderRadius: 6
    },
    iconMuted: { color: MUTED },
    cardSelected: {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
    },
    // Sol şerit: gelen nötr, giden yeşil (seçili kart çerçevesinin üstüne yazar).
    cardIn: { borderLeft: `4px solid ${IDLE}` },
    // Giden kart: temanın success renginden açık yeşil zemin (gelen kart beyaz kalır).
    cardOut: { borderLeft: `4px solid ${OK}`, backgroundColor: fade(theme.palette.success.main, 0.08) },
    cardWho: { minWidth: 0 },
    cardHead: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '9px 12px',
      cursor: 'pointer'
    },
    cardSender: { fontSize: FS_BASE, fontWeight: 500, color: theme.palette.text.primary },
    cardAddr: { fontSize: FS_SMALL, color: MUTED },
    dirPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      fontFamily: font,
      fontSize: 11,
      fontWeight: 500,
      lineHeight: 1.5,
      padding: '1px 7px',
      borderRadius: 10,
      backgroundColor: TINT
    },
    dirIcon: { fontSize: 13 },
    dirPillOut: { backgroundColor: OK_BG, color: OK },
    cardDate: {
      fontSize: FS_SMALL,
      color: MUTED,
      marginLeft: 'auto',
      whiteSpace: 'nowrap'
    },
    cardPreview: {
      padding: '0 12px 10px 40px',
      display: 'flex',
      alignItems: 'center',
      gap: 12
    },
    cardPreviewText: {
      flex: 1,
      minWidth: 0,
      fontSize: FS_SMALL,
      color: MUTED,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    cardShowBtn: { flexShrink: 0 },
    cardBody: { padding: '4px 12px 12px 40px' },
    // Gövde: tema fontu, 14 px / 1.5. Düz metin (pre/tablo) de aynı fontu devralır; içerik değiştirilmez.
    html: {
      fontFamily: font,
      fontSize: FS_BASE,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      overflowWrap: 'anywhere',
      '& pre, & table, & td, & th, & code': { fontFamily: 'inherit' },
      '& pre': { whiteSpace: 'pre-wrap', margin: 0 },
      '& img': { maxWidth: '100%' },
      '& > :last-child': { marginBottom: 0 }
    },
    attachment: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '4px 0',
      fontSize: FS_BASE
    },
    // Müşteri alanı: üstte soluk etiket, solda değer, sağda üç yuvarlak (30 px) ikon düğmesi.
    fieldRow: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '2px 8px',
      alignItems: 'center',
      marginBottom: 12
    },
    fieldLabel: { gridColumn: '1 / -1', fontSize: FS_SMALL, color: MUTED },
    fieldValue: {
      fontSize: FS_BASE,
      color: theme.palette.text.primary,
      fontVariantNumeric: 'tabular-nums',
      overflowWrap: 'anywhere'
    },
    fieldEdit: {
      gridColumn: '1 / -1',
      display: 'flex',
      gap: 6,
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    fieldTools: { display: 'flex', gap: 2, alignItems: 'center' },
    fieldIconBtn: {
      width: 30,
      height: 30,
      padding: 0,
      color: MUTED,
      '&:hover': { backgroundColor: TINT, color: theme.palette.text.primary }
    },
    fieldBulk: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4, marginBottom: 12 },
    stateConfirmed: {
      height: 20,
      fontSize: FS_SMALL,
      backgroundColor: 'transparent',
      color: OK
    },
    fieldActions: { display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' },
    historyItem: {
      padding: '8px 0',
      borderTop: `1px solid ${LINE}`,
      fontSize: 13,
      '&:first-of-type': { borderTop: 'none' }
    }
  };
});
